// Main entry — orchestrates renderer, overlays, audio, freeze-share, hold-still,
// arcade, reduced-motion, DPR auto-scale, Service Worker. The shader stays the
// only thing that knows about uniforms; everything else composes.

import { WebGL2FeedbackRenderer } from './renderers/webgl2';
import { FALLBACK_PARAMETERS, ParametersSchema, type Parameters } from './lib/parameters-schema';
import { mountOverlays, type OverlayHandles } from './overlays';
import { getStableVisitorId, deriveVisitorSeed } from './seed/per-visitor';
import { createAudioReactor } from './audio/reactor';
import { bindSoundToggle } from './input/sound-toggle';
import { bindHoldStill } from './input/hold-still';
import { bindFreezeShare } from './share/freeze';
import { bindKonami } from './input/konami';
import { createArcade } from './arcade/game';
import { applyReducedMotion, watchMotionPreference } from './input/reduced-motion';
import { makeAutoScale } from './perf/auto-scale';

declare global {
  interface Window {
    __HERMINATOR_NO_WEBGL2__?: boolean;
  }
}

function resolveParametersUrl(): string {
  if (typeof document !== 'undefined') {
    const meta = document.querySelector('meta[name="herminator:parameters-url"]')?.getAttribute('content');
    if (meta) return meta;
  }
  if (typeof location !== 'undefined') {
    const host = location.hostname;
    const isDev = host === 'localhost' || host === '127.0.0.1' || host.includes('herminator-dev') || host.endsWith('-dev.run.app');
    if (isDev) return 'https://assets.ayfie.ai/herminator/dev/parameters.json';
  }
  return 'https://assets.ayfie.ai/herminator/parameters.json';
}

const PARAMETERS_URL = resolveParametersUrl();

async function loadParameters(): Promise<Parameters> {
  try {
    const res = await fetch(PARAMETERS_URL, { cache: 'no-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json: unknown = await res.json();
    return ParametersSchema.parse(json);
  } catch (err) {
    console.warn('[herminator] parameters.json fetch failed; using fallback:', err);
    return FALLBACK_PARAMETERS;
  }
}

async function bootstrap(): Promise<void> {
  if (window.__HERMINATOR_NO_WEBGL2__) return;

  const canvas = document.getElementById('canvas');
  if (!(canvas instanceof HTMLCanvasElement)) return;

  const renderer = new WebGL2FeedbackRenderer();
  await renderer.init(canvas);

  const handleResize = (): void => {
    renderer.resize({ width: window.innerWidth, height: window.innerHeight });
  };
  window.addEventListener('resize', handleResize, { passive: true });
  handleResize();

  // Per-visitor seed — small hue offset compensates for daily-cadence "frozen"
  // feel between :05 ticks. Two visitors on the same day see different shades.
  const visitorId = getStableVisitorId();
  let currentParams: Parameters = applyReducedMotion(FALLBACK_PARAMETERS);
  let seed = deriveVisitorSeed(visitorId, currentParams.cycle);
  renderer.setSeedOffset(seed.hueOffsetDeg);
  renderer.setParameters(currentParams, 0);

  // Mount DOM overlays (watermark, cycle, ticker, sound chip, info panel)
  const overlays: OverlayHandles = mountOverlays(document.body, currentParams);
  setTimeout(() => overlays.showMoveHint(), 600);
  setTimeout(() => overlays.hideMoveHint(), 4500);

  // Audio reactor
  const audio = createAudioReactor(visitorId);
  audio.setParameters(currentParams);
  bindSoundToggle(overlays, async () => { await audio.arm(); });

  // Hold-still detector
  const holdStill = bindHoldStill(document.body);

  // Freeze-frame share — hoisted so the arcade can reuse trigger() for [S]
  // on the end-of-round summary card.
  const freezeShare = bindFreezeShare(canvas, () => currentParams);

  // Konami arcade — visitorSeed is callable so the binding stays live across
  // the loadParameters() reassignment of `seed` further down.
  const arcade = createArcade(() => currentParams, {
    renderer,
    audio,
    freezeShare,
    visitorSeed: () => seed,
  });
  bindKonami(() => arcade.start(), {
    isBlocked: () => arcade.isRunning() || overlays.isInfoPanelOpen(),
  });

  // Expose the renderer for the smoke harness + dev console inspection.
  // The smoke harness reads arcadeIntensity/flashStarts/uniform values to
  // verify the WCAG flash limiter and arcade-mode ramp without needing
  // additional debug-only API surface.
  (window as Window & { __renderer?: WebGL2FeedbackRenderer }).__renderer = renderer;

  // Watch reduced-motion changes — re-apply current parameters when toggled
  watchMotionPreference(() => {
    const adjusted = applyReducedMotion(currentParams);
    renderer.setParameters(adjusted, 0);
  });

  // DPR auto-scale: warm-up frame budget check
  const autoScale = makeAutoScale((mult) => renderer.setDprMultiplier(mult));

  // Cursor input feeds shader uniforms + audio + hold-still.
  //
  // Initial sentinel (-1, -1) — out of UV bounds — so the shader gates splash
  // + drift to zero before the user's first real pointer event. Without this,
  // cold-load on mobile (and shared `#arcade=...` URLs where the user hasn't
  // touched yet) bakes a permanent center bloom under max-blend feedback.
  // First real updateFromPointer() call replaces both x and y with [0,1] coords.
  const input = { x: -1, y: -1, pressure: 0 };
  const updateFromPointer = (clientX: number, clientY: number, pressure: number): void => {
    input.x = clientX / window.innerWidth;
    input.y = 1 - clientY / window.innerHeight;
    input.pressure = pressure;
    renderer.setInput(input);
    audio.setCursor(input.x, input.y, pressure);
    holdStill.noteInput();
    overlays.hideMoveHint();
  };
  renderer.setInput(input);

  window.addEventListener('pointermove', (e) => {
    updateFromPointer(e.clientX, e.clientY, e.pressure || 0);
  }, { passive: true });
  window.addEventListener('pointerdown', (e) => {
    updateFromPointer(e.clientX, e.clientY, 1);
  }, { passive: true });
  window.addEventListener('pointerup', () => {
    input.pressure = 0;
    renderer.setInput(input);
    audio.setCursor(input.x, input.y, 0);
  }, { passive: true });
  // Touch falls through to pointer events on modern browsers; explicit
  // touchmove guards iOS Safari quirks where pointermove can be flaky during
  // active gestures.
  window.addEventListener('touchmove', (e) => {
    const t = e.touches[0];
    if (t) updateFromPointer(t.clientX, t.clientY, 1);
  }, { passive: true });

  // RAF loop
  let raf = 0;
  let lastFrameMs = performance.now();
  const frame = (now: number): void => {
    const delta = now - lastFrameMs;
    lastFrameMs = now;
    autoScale.noteFrame(delta);
    renderer.frame(now);
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);

  // Live parameters: morph from fallback to GCS-served once it lands
  void loadParameters().then((params) => {
    currentParams = applyReducedMotion(params);
    seed = deriveVisitorSeed(visitorId, currentParams.cycle);
    renderer.setSeedOffset(seed.hueOffsetDeg);
    renderer.setParameters(currentParams, 4);
    overlays.setParameters(currentParams);
    audio.setParameters(currentParams);
  });

  // Service Worker — last-good cache for resilience under Vertex/GCS hiccups.
  // Vite produces /sw.js from src/sw.ts via the entry config (see vite.config.ts).
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch((err) => {
        console.warn('[herminator] sw register failed:', err);
      });
    });
  }

  window.addEventListener('beforeunload', () => {
    cancelAnimationFrame(raf);
    audio.destroy();
    holdStill.destroy();
    renderer.destroy();
  });
}

bootstrap().catch((err: unknown) => {
  console.error('[herminator] bootstrap failed:', err);
});
